import React, { useState } from 'react';
import {
  Card, CardContent, Typography, Chip, Stack, Button, Box,
  Dialog, DialogTitle, DialogContent, IconButton,
} from '@mui/material';
import SourceChip from './SourceChip';

function formatLabel(value) {
  if (!value) return '';
  return value
    .toString()
    .replace(/[_-]+/g, ' ')
    .trim()
    .split(' ')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
}

function ExternalLinkIcon() {
  return (
    <Box component="svg" viewBox="0 0 16 16" sx={{ width: 14, height: 14, fill: 'none', stroke: 'currentColor', strokeWidth: 1.5 }}>
      <path d="M6 3H3.5A1.5 1.5 0 0 0 2 4.5v8A1.5 1.5 0 0 0 3.5 14h8a1.5 1.5 0 0 0 1.5-1.5V10" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M9.5 2H14v4.5M14 2 7 9" strokeLinecap="round" strokeLinejoin="round" />
    </Box>
  );
}

function CloseIcon() {
  return (
    <Box component="svg" viewBox="0 0 16 16" sx={{ width: 16, height: 16, fill: 'none', stroke: 'currentColor', strokeWidth: 1.5 }}>
      <path d="M3 3l10 10M13 3 3 13" strokeLinecap="round" />
    </Box>
  );
}

export default function JobCard({ job }) {
  const [descriptionOpen, setDescriptionOpen] = useState(false);

  // Raw JobsPipe/Techmap shape uses either field name depending on the source board —
  // same fallback jobMapper.js uses when persisting a saved job.
  const employmentType = job.employment_type || job.job_type;

  return (
    <>
      <Card variant="outlined" sx={{ mb: 1.75, transition: 'box-shadow 0.15s ease', '&:hover': { boxShadow: 1 } }}>
        <CardContent sx={{ '&:last-child': { pb: 2.5 } }}>

          {/* Title/company on the left, source badge pushed to the true right edge */}
          <Box sx={{ display: 'flex', width: '100%', justifyContent: 'space-between', alignItems: 'flex-start', gap: 2 }}>
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="subtitle1" sx={{ lineHeight: 1.3 }}>
                {job.job_title}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
                {job.company}{job.company && job.location ? ' · ' : ''}{job.location}
              </Typography>
            </Box>

            {job.source && (
              <Box sx={{ flexShrink: 0 }}>
                <SourceChip source={job.source} />
              </Box>
            )}
          </Box>

          {/* Tags + "View description" on the left, "View posting" pushed to the true right edge */}
          <Box
            sx={{
              display: 'flex',
              width: '100%',
              justifyContent: 'space-between',
              alignItems: 'center',
              flexWrap: 'wrap',
              rowGap: 1,
              mt: 2,
            }}
          >
            <Stack direction="row" spacing={1} flexWrap="wrap" alignItems="center" sx={{ rowGap: 1 }}>
              {job.seniority && (
                <Chip label={formatLabel(job.seniority)} size="small" variant="outlined" />
              )}
              {employmentType && (
                <Chip label={formatLabel(employmentType)} size="small" variant="outlined" />
              )}
              {job.remote && <Chip label="Remote" size="small" variant="outlined" />}
              {job.description && (
                <Button
                  size="small"
                  variant="outlined"
                  color="inherit"
                  onClick={() => setDescriptionOpen(true)}
                  sx={{ height: 26, fontSize: 12 }}
                >
                  View description
                </Button>
              )}
            </Stack>

            <Button
              href={job.url}
              target="_blank"
              rel="noopener"
              variant="contained"
              size="small"
              endIcon={<ExternalLinkIcon />}
              sx={{ flexShrink: 0 }}
            >
              View posting
            </Button>
          </Box>

        </CardContent>
      </Card>

      <Dialog open={descriptionOpen} onClose={() => setDescriptionOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ pr: 6 }}>
          {job.job_title}
          <Typography variant="body2" color="text.secondary">
            {job.company}{job.company && job.location ? ' · ' : ''}{job.location}
          </Typography>
          <IconButton onClick={() => setDescriptionOpen(false)} sx={{ position: 'absolute', right: 12, top: 12 }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" sx={{ whiteSpace: 'pre-line' }}>
            {job.description}
          </Typography>
        </DialogContent>
      </Dialog>
    </>
  );
}
